import React, { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ENTITY, MAIN_SITE_IMAGE, WEBSITE } from 'src/consts'
import {
  getCoinsActivePeriod,
  getCoinsGlobalSelector,
  getCurrentCurrencySelector,
} from 'src/redux/selectors'
import { ListLayout } from 'src/layouts'
import { TableView } from 'src/components/TableView'
import CoinsFilterLayout from 'src/layouts/CoinsFilterLayout'
import useWidth from 'src/hooks/useWidth'
import useFetchMapData from 'src/hooks/useFetchMapData'
import { getWatchlistTabs } from 'src/redux/thunks'
import { intl } from 'src/i18n'
import {
  useConnectedState,
  useConnectedStateTop10List,
} from 'src/components/TableView/hooks/redux'

const Coin = () => {
  const dispatch = useDispatch()
  const layoutRef = useRef(null)
  const currency = useSelector(getCurrentCurrencySelector)
  const activePeriod = useSelector(getCoinsActivePeriod)
  const connectedState = useConnectedState(ENTITY.COIN)
  const coinsData = connectedState.data
  const globalData = useSelector(getCoinsGlobalSelector)
  const { isMobile } = useWidth()
  const defaultConnectedStateTop10 = useConnectedStateTop10List(ENTITY.COIN)

  useEffect(() => {
    dispatch(getWatchlistTabs())
  }, [dispatch])

  useFetchMapData({ entity: ENTITY.COIN })

  const onPageChange = () => {
    layoutRef.current?.scrollToTop()
  }

  const title = intl.t('coin.seo.title')
  const description = intl.t('coin.seo.description')
  const uri = `/coin`

  const itemListElement = defaultConnectedStateTop10.data.map(
    (coin, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Thing',
        name: coin.name,
        description: coin.description,
        additionalType: [
          {
            '@type': 'PropertyValue',
            name: 'Market Cap',
            value: coin.marketCap,
          },
          {
            '@type': 'PropertyValue',
            name: 'Price',
            value: coin.price,
          },
          {
            '@type': 'PropertyValue',
            name: 'Volume (24h)',
            value: coin.volume,
          },
          {
            '@type': 'PropertyValue',
            name: 'Circulating Supply',
            value: coin.circulatingSupply,
          },
          {
            '@type': 'PropertyValue',
            name: 'Change (%, 24h)',
            value: coin.change,
          },
        ],
      },
    })
  )

  const pageMeta = (
    <script
      type='application/ld+json'
      id='Main-Script'
      dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          '@context': 'http://schema.org/',
          '@type': 'WebPage',
          name: title,
          description: description,
          url: `${WEBSITE}${uri}`,
          mainEntity: {
            '@type': 'ItemList',
            name: 'Cryptocurrency List',
            description: 'List of cryptocurrencies with their market data',
            numberOfItems: defaultConnectedStateTop10.data.length,
            itemListElement: itemListElement,
          },
          publisher: {
            '@type': 'Organization',
            name: 'Coin360',
            description: intl.t('coin.publisher.description'),
            url: WEBSITE,
            logo: MAIN_SITE_IMAGE,
            sameAs: [
              'https://www.reddit.com/r/Coin360',
              'https://twitter.com/coin360com',
              'https://www.facebook.com/Coin360com/',
              'https://t.me/COIN360com',
              'https://mt.linkedin.com/company/coin360',
              'https://www.tiktok.com/@coin360com?lang=en',
              'https://www.youtube.com/@COIN-li4tr/about',
            ],
          },
        }),
      }}
    />
  )

  return (
    <ListLayout
      ref={layoutRef}
      title={title}
      description={description}
      uri={uri}
      pageMeta={pageMeta}
      scrollToTop
    >
      {!isMobile && <CoinsFilterLayout coinsPage={true} />}
      <TableView
        currency={currency}
        onPageChange={onPageChange}
        activePeriod={activePeriod}
        entity={ENTITY.COIN}
        entitiesData={coinsData}
        globalData={globalData}
      />
    </ListLayout>
  )
}

export default Coin
